import { useLanguage } from "@/LanguageContext";
import { motion } from "framer-motion";

const GIADA_IMG =
  "https://customer-assets.emergentagent.com/job_senior-care-valais/artifacts/gyhnip5r_c71ea6bc-0dd3-424f-9a38-04d3dfc816a9.jpg";

const aboutText = {
  fr: {
    label: "À propos",
    title: "Une présence humaine, avant tout",
    paragraphs: [
      "Giada Vento a fait un choix simple, mais essentiel : remettre l'humain au centre de l'accompagnement.",
      "Issue du domaine de la santé, elle a accompagné de nombreuses personnes âgées et personnes à mobilité réduite et en situation de handicap (physique et psychique) au quotidien. Avec le temps, une évidence s'est imposée : au-delà des soins, ce sont la présence, l'écoute et le lien humain qui font toute la différence.",
      "C'est ainsi qu'est née Présence & Sourire.",
      "Aujourd'hui, Giada propose un accompagnement personnalisé, basé sur la confiance, le respect du rythme de chacun et une attention sincère.",
      "Parce que vieillir ne signifie pas arrêter de vivre.",
    ],
  },
  de: {
    label: "Über uns",
    title: "Menschliche Präsenz, vor allem",
    paragraphs: [
      "Giada Vento hat eine einfache, aber wesentliche Entscheidung getroffen: den Menschen wieder in den Mittelpunkt der Begleitung zu stellen.",
      "Aus dem Gesundheitsbereich kommend, hat sie zahlreiche ältere Menschen im Alltag begleitet. Mit der Zeit wurde eine Erkenntnis unausweichlich: Über die Pflege hinaus sind es Präsenz, Zuhören und menschliche Verbindung, die den Unterschied machen.",
      "So entstand Présence & Sourire.",
      "Heute bietet Giada eine persönliche Begleitung an, die auf Vertrauen, Respekt vor dem Rhythmus jedes Einzelnen und aufrichtiger Aufmerksamkeit basiert.",
      "Weil Älterwerden nicht bedeutet, aufzuhören zu leben.",
    ],
  },
  en: {
    label: "About Us",
    title: "A human presence, above all",
    paragraphs: [
      "Giada Vento made a simple but essential choice: to put the human back at the heart of accompaniment.",
      "Coming from the healthcare field, she accompanied many elderly people in their daily lives. Over time, one truth became clear: beyond care, it is presence, listening and human connection that make all the difference.",
      "This is how Présence & Sourire was born.",
      "Today, Giada offers personalized accompaniment based on trust, respect for each person's rhythm and sincere attention.",
      "Because aging does not mean stopping to live.",
    ],
  },
};

export default function AboutSection() {
  const { language } = useLanguage();
  const content = aboutText[language] || aboutText.fr;

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 lg:py-32 bg-[#E8E0D4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm font-body uppercase tracking-[0.2em] text-[#6FA8B6] mb-4">
            {content.label}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C3E50]">
            {content.title}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="flex justify-center"
          >
            <div className="w-[300px] h-[380px] sm:w-[350px] sm:h-[440px] rounded-3xl overflow-hidden shadow-xl">
              <img
                src={GIADA_IMG}
                alt="Giada Vento – Fondatrice de Présence & Sourire"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="space-y-5"
          >
            {content.paragraphs.map((p, i) => (
              <p
                key={i}
                className={`font-body text-base lg:text-lg leading-relaxed ${
                  i === 2
                    ? "text-[#6FA8B6] font-medium font-heading text-xl"
                    : i === content.paragraphs.length - 1
                    ? "text-[#2C3E50] font-medium italic"
                    : "text-[#2C3E50]/75"
                }`}
              >
                {p}
              </p>
            ))}
            <p className="font-body text-sm text-[#2C3E50]/50 pt-2">
              — Giada Vento, Fondatrice
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
